'use client';
/** @jsxImportSource @emotion/react */

import {
  useContext,
  createContext,
  useMemo,
  type FC,
  type PropsWithChildren,
  memo,
} from 'react';
import {
  type FunctionComponent,
  type LazyExoticComponent,
  type SVGProps,
} from 'react';

import { type coreIcons } from '../core-icon/icons';

type LazyIconModule = LazyExoticComponent<
  FunctionComponent<SVGProps<SVGSVGElement>>
>;

type Icons = { [key: string]: LazyIconModule } & {
  [key in keyof typeof coreIcons]: LazyIconModule;
};

const IconContext = createContext<{ icons?: Icons }>({});

interface Props {
  icons?: Icons;
}

export const IconProvider: FC<PropsWithChildren<Props>> = memo(
  ({ children, icons }) => {
    const memoisedValue = useMemo(() => ({ icons }), [icons]);
    return (
      <IconContext.Provider value={memoisedValue}>
        {children}
      </IconContext.Provider>
    );
  }
);

export const useIconContext = () => {
  const context = useContext(IconContext);

  if (!context) {
    throw new Error(
      'Attempted to render a Zeus icon component outside of <ZeusProvider>'
    );
  }

  return context;
};
